import React, { useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  Paper,
  Pagination
} from '@mui/material';
import {
  History as HistoryIcon,
  Search as SearchIcon,
  Schedule as TimeIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSearchStore } from '../../stores/useSearchStore';
import type { SearchHistory as SearchHistoryType } from '../../types/search';

export const SearchHistory: React.FC = () => {
  const navigate = useNavigate();
  const {
    searchHistory,
    historyTotalCount,
    historyPage,
    historyPageSize,
    isHistoryLoading,
    historyError,
    loadSearchHistory,
    setSearchRequest,
    search
  } = useSearchStore();

  useEffect(() => {
    loadSearchHistory(1);
  }, [loadSearchHistory]);

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    loadSearchHistory(page);
  };

  const handleHistoryClick = async (historyItem: SearchHistoryType) => {
    // Set the search request based on the history item
    const filters = historyItem.filters ? (typeof historyItem.filters === 'string' ? JSON.parse(historyItem.filters) : historyItem.filters) : {};

    setSearchRequest({
      query: historyItem.query,
      startDate: filters.startDate || '',
      endDate: filters.endDate || '',
      fileType: filters.fileType || '',
      author: filters.author || '',
      tags: filters.tags || [],
      sortBy: 'relevance',
      sortOrder: 'desc',
      page: 1,
      pageSize: 20
    });

    // Navigate to search page with search results tab
    navigate('/search?tab=0');

    // Then trigger search
    await search();
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));

    if (diffInHours < 1) {
      return 'Vừa xong';
    } else if (diffInHours < 24) {
      return `${diffInHours} giờ trước`;
    } else if (diffInHours < 48) {
      return 'Hôm qua';
    } else {
      return date.toLocaleDateString('vi-VN');
    }
  };

  const parseFilters = (filtersRaw?: any) => {
    if (!filtersRaw) return null;

    try {
      const filters = typeof filtersRaw === 'string' ? JSON.parse(filtersRaw) : filtersRaw;
      const activeFilters = [];

      if (filters.startDate || filters.endDate) {
        activeFilters.push('Khoảng ngày');
      }
      if (filters.fileType) {
        activeFilters.push(`Loại: ${filters.fileType}`);
      }
      if (filters.author) {
        activeFilters.push(`Tác giả: ${filters.author}`);
      }
      if (filters.tags && filters.tags.length > 0) {
        activeFilters.push(`${filters.tags.length} nhãn`);
      }

      return activeFilters;
    } catch {
      return null;
    }
  };

  const totalPages = Math.ceil(historyTotalCount / historyPageSize);

  if (isHistoryLoading && searchHistory.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (historyError) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {historyError}
      </Alert>
    );
  }

  if (!Array.isArray(searchHistory) || searchHistory.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <HistoryIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          Chưa có lịch sử tìm kiếm
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Các tìm kiếm gần đây sẽ xuất hiện ở đây
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <HistoryIcon sx={{ mr: 1 }} />
            <Typography variant="h6">
              Lịch Sử Tìm Kiếm
            </Typography>
          </Box>

          <List>
            {searchHistory.map((historyItem, index) => {
              const activeFilters = parseFilters(historyItem.filters);

              return (
                <React.Fragment key={historyItem.id}>
                  <ListItem disablePadding>
                    <ListItemButton
                      onClick={() => handleHistoryClick(historyItem)}
                      sx={{
                        borderRadius: 1,
                        mb: 0.5,
                        '&:hover': {
                          backgroundColor: 'action.hover'
                        }
                      }}
                    >
                      <ListItemIcon>
                        <SearchIcon />
                      </ListItemIcon>

                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                              {historyItem.query}
                            </Typography>
                            <Chip
                              icon={<TimeIcon />}
                              label={formatTimestamp(historyItem.timestamp)}
                              size="small"
                              variant="outlined"
                              color="default"
                            />
                          </Box>
                        }
                        secondary={
                          <Typography component="div" variant="body2">
                            {activeFilters && activeFilters.length > 0 && (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                {activeFilters.map((filter, filterIndex) => (
                                  <Chip
                                    key={filterIndex}
                                    label={filter}
                                    size="small"
                                    variant="outlined"
                                    color="secondary"
                                  />
                                ))}
                              </Box>
                            )}
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  </ListItem>

                  {index < searchHistory.length - 1 && <Divider />}
                </React.Fragment>
              );
            })}
          </List>
        </CardContent>
      </Card>

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination
            count={totalPages}
            page={historyPage}
            onChange={handlePageChange}
            color="primary"
          />
        </Box>
      )}
    </Box>
  );
};
